import { useEffect, useRef, useState } from 'react';
import { getSupabase } from '../client';
import type { CurrentUserProfile } from '../hooks/useCurrentUserProfile';

/**
 * UserMenuDropdown — menú al click en el chip de usuario del topbar.
 *
 * Anchor: el propio chip (botón con role="button"). Panel: similar a
 * NotificacionesDropdown — click-outside cierra, ESC cierra. Muestra:
 *   - Header con nombre display (nickname || nombre) + email + rol.
 *   - Editor inline de nickname (input edit-in-place + Guardar/Cancelar).
 *   - Botón "Cambiar contraseña" que delega al host (ChangePasswordModal).
 *
 * El host es responsable de montar ChangePasswordModal vía `onChangePassword`.
 */
interface Props {
  user: CurrentUserProfile;
  onChangePassword: () => void;
  /** Llamado tras guardar nickname (para que el host refresque su cache). */
  onNicknameSaved?: () => void;
}

const ROL_LABEL: Record<string, string> = {
  admin_aosenuma: 'Aosenuma · Admin',
  tecnico_aosenuma: 'Aosenuma · Técnico',
  auditor: 'Auditor',
  cliente_elawan: 'Cliente · Elawan',
};

const MAX_NICK = 40;

export function UserMenuDropdown({ user, onChangePassword, onNicknameSaved }: Props) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const [editing, setEditing] = useState(false);
  const [nickDraft, setNickDraft] = useState(user.nickname ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset draft cuando cambia el user (cambio de cuenta) o cierras el dropdown.
  useEffect(() => { setNickDraft(user.nickname ?? ''); }, [user.nickname]);

  // Click-outside cierra (mismo patrón que NotificacionesDropdown).
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!wrapRef.current?.contains(t)) {
        setOpen(false);
        setEditing(false);
        setError(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // ESC cierra.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (editing) { setEditing(false); setNickDraft(user.nickname ?? ''); setError(null); }
        else setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, editing, user.nickname]);

  async function handleSaveNick() {
    setSaving(true);
    setError(null);
    try {
      const clean = nickDraft.trim();
      const sb = getSupabase();
      const { error: rpcErr } = await sb.rpc('update_my_nickname', {
        p_nickname: clean === '' ? null : clean,
      });
      if (rpcErr) {
        if (rpcErr.message?.includes('invalid_nickname_length')) {
          throw new Error(`El nickname debe tener entre 1 y ${MAX_NICK} caracteres.`);
        }
        throw rpcErr;
      }
      setEditing(false);
      onNicknameSaved?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar el nickname.');
    } finally {
      setSaving(false);
    }
  }

  function handleCancelNick() {
    setNickDraft(user.nickname ?? '');
    setEditing(false);
    setError(null);
  }

  const displayName = (user.nickname && user.nickname.trim()) || user.nombre;
  const rolLabel = ROL_LABEL[user.rol_app] ?? user.rol_app;

  return (
    <div className="user-menu-dropdown" ref={wrapRef}>
      <button
        type="button"
        className="aosenuma-topbar__user-chip user-menu-dropdown__anchor"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        title="Mi cuenta"
      >
        <span className="aosenuma-topbar__user-label">{displayName}</span>
        <svg
          viewBox="0 0 24 24" width={11} height={11} fill="none"
          stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round"
          aria-hidden style={{ marginLeft: 4, opacity: 0.6 }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="user-menu-dropdown__panel" role="menu" aria-label="Menú de usuario">
          <div className="user-menu-dropdown__header">
            <div className="user-menu-dropdown__name">{displayName}</div>
            <div className="user-menu-dropdown__email">{user.email}</div>
            <div className="user-menu-dropdown__rol">{rolLabel}</div>
          </div>

          <div className="user-menu-dropdown__row">
            <div className="user-menu-dropdown__row-label">Nickname</div>
            {editing ? (
              <>
                <input
                  type="text"
                  value={nickDraft}
                  onChange={(e) => { setNickDraft(e.target.value); setError(null); }}
                  maxLength={MAX_NICK}
                  placeholder={user.nombre}
                  autoFocus
                  className="user-menu-dropdown__nick-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); void handleSaveNick(); }
                  }}
                />
                <div className="user-menu-dropdown__row-actions">
                  <button
                    type="button"
                    onClick={handleCancelNick}
                    disabled={saving}
                    className="user-menu-dropdown__btn user-menu-dropdown__btn--ghost"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleSaveNick()}
                    disabled={saving}
                    className="user-menu-dropdown__btn user-menu-dropdown__btn--primary"
                  >
                    {saving ? 'Guardando…' : 'Guardar'}
                  </button>
                </div>
                {error && (
                  <div role="alert" className="user-menu-dropdown__error">{error}</div>
                )}
              </>
            ) : (
              <>
                <div className="user-menu-dropdown__nick-value">
                  {user.nickname && user.nickname.trim()
                    ? user.nickname
                    : <span className="user-menu-dropdown__nick-placeholder">— sin nickname —</span>}
                </div>
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="user-menu-dropdown__btn user-menu-dropdown__btn--ghost"
                >
                  Editar
                </button>
              </>
            )}
          </div>

          <div className="user-menu-dropdown__divider" />

          <button
            type="button"
            onClick={() => { setOpen(false); onChangePassword(); }}
            className="user-menu-dropdown__action"
            role="menuitem"
          >
            <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ marginRight: 8, opacity: 0.7 }}>
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            Cambiar contraseña
          </button>
        </div>
      )}
    </div>
  );
}
